import { FormEvent, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { apiClient } from '../api/client'
import { DetailErrorState, DetailLoadingState } from '../components/StateComponents'
import { useEvent } from '../hooks/useEvents'
import { useLanguage } from '../hooks/useLanguage'
import type { PolicyAnalysisResponse, PolicyChatResponse } from '../types/policyAnalysis'
import { getAnalyticsContext, trackBusinessEvent } from '../utils/analytics'
import { getTelegramUserId } from '../utils/telegram'
import type { EventDetail, EventSummary } from '../types/event'

function Citations({ values }: { values: string[] }) {
  return <span className="ml-1 text-xs font-semibold text-blue-600 dark:text-blue-400">[{values.join(', ')}]</span>
}

export function PolicyAnalysisPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const date = searchParams.get('date') || 'latest'
  const { lang, t } = useLanguage()
  const [region, setRegion] = useState<'singapore'>('singapore')
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([])
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const { data: event, isLoading: eventLoading, error: eventError } = useEvent(id, date)
  const routeEvent = (location.state as { event?: EventSummary } | null)?.event
  const displayedEvent = event || routeEvent
  const query = useQuery({
    queryKey: ['policy-analysis', id, date, lang, region],
    queryFn: () => {
      const userId = getTelegramUserId()
      return apiClient.post<PolicyAnalysisResponse>(`/api/policy-analysis/${id}?date=${encodeURIComponent(date)}`, {
        user_id: /^\d+$/.test(userId) ? userId : null,
        language: lang,
        region,
        analytics_context: getAnalyticsContext(),
      })
    },
    enabled: !!id,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const question = input.trim()
    const sessionId = query.data?.session_id
    if (!question || !id || !sessionId || isSending) return
    setInput('')
    setMessages((current) => [...current, { role: 'user', content: question }])
    setIsSending(true)
    trackBusinessEvent('policy_chat_send', { event_id: id, region, message_length: question.length }, id)
    try {
      const userId = getTelegramUserId()
      const response = await apiClient.post<PolicyChatResponse>(`/api/policy-analysis/${id}/chat`, {
        session_id: sessionId,
        message: question,
        user_id: /^\d+$/.test(userId) ? userId : null,
        analytics_context: getAnalyticsContext(),
      })
      if (!response.success || !response.reply) throw new Error(response.error || t('追问失败', 'Follow-up failed'))
      setMessages((current) => [...current, { role: 'assistant', content: response.reply! }])
      trackBusinessEvent('policy_chat_success', { event_id: id, region, reply_length: response.reply.length }, id)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setMessages((current) => [...current, { role: 'assistant', content: message }])
      trackBusinessEvent('policy_chat_fail', { event_id: id, region, error: message }, id)
    } finally {
      setIsSending(false)
    }
  }

  useEffect(() => {
    if (!query.data || !id) return
    trackBusinessEvent(query.data.cached ? 'policy_analysis_cache_hit' : 'policy_analysis_generated', {
      event_id: id,
      date,
      display_language: lang,
      retrieval_language: query.data.retrieval_language,
      source_count: query.data.sources.length,
      status: query.data.status,
    }, id)
  }, [query.data, id, date, lang])

  useEffect(() => {
    if (!query.error || !id) return
    trackBusinessEvent('policy_analysis_failed', {
      event_id: id,
      date,
      error: query.error instanceof Error ? query.error.message : String(query.error),
    }, id)
  }, [query.error, id, date])

  if (eventLoading && !displayedEvent) return <DetailLoadingState />
  if ((eventError && !displayedEvent) || !displayedEvent) return <DetailErrorState onBack={() => navigate(-1)} />
  const whatHappened = (displayedEvent as Partial<EventDetail>).what_happened

  const pageHeader = (
    <header className="sticky top-0 z-20 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate('/')}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800"
          aria-label={t('返回', 'Back')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="font-semibold text-gray-900 dark:text-white">{t('政策解读', 'Policy Analysis')}</h1>
        </div>
        <select
          value={region}
          onChange={(changeEvent) => {
            setRegion(changeEvent.target.value as 'singapore')
            setMessages([])
            if (id) trackBusinessEvent('policy_region_change', { event_id: id, region: changeEvent.target.value }, id)
          }}
          aria-label={t('政策地区', 'Policy region')}
          className="max-w-[112px] rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
        >
          <option value="singapore">{t('新加坡', 'Singapore')}</option>
          <option value="us" disabled>{t('美国（即将上线）', 'US (soon)')}</option>
          <option value="eu" disabled>{t('欧盟（即将上线）', 'EU (soon)')}</option>
          <option value="china" disabled>{t('中国（即将上线）', 'China (soon)')}</option>
        </select>
      </div>
    </header>
  )

  const eventSection = (
    <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <h2 className="text-base font-semibold leading-snug text-gray-900 dark:text-white">{displayedEvent.title}</h2>
      {displayedEvent.background && <div className="mt-3"><h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('背景：', 'Background:')}</h3><p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-gray-600 dark:text-gray-300">{displayedEvent.background}</p></div>}
      {whatHappened && whatHappened !== displayedEvent.background && <div className="mt-3"><h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('事件详情：', 'Event details:')}</h3><p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-gray-600 dark:text-gray-300">{whatHappened}</p></div>}
      {!displayedEvent.background && !whatHappened && displayedEvent.summary && <div className="mt-3"><h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('事件详情：', 'Event details:')}</h3><p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-gray-600 dark:text-gray-300">{displayedEvent.summary}</p></div>}
    </section>
  )

  if (query.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        {pageHeader}
        <main className="mx-auto max-w-3xl space-y-4 p-4">
          {eventSection}
          <section className="rounded-lg border border-blue-100 bg-white p-4 dark:border-blue-900 dark:bg-gray-900">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
              <div>
                <h2 className="text-sm font-medium text-gray-900 dark:text-white">{t('正在进行政策解读', 'Analyzing policy implications')}</h2>
              </div>
            </div>
          </section>
        </main>
      </div>
    )
  }

  if (query.error || !query.data) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        {pageHeader}
        <main className="mx-auto max-w-3xl space-y-4 p-4">
          {eventSection}
        <div className="rounded-lg border border-red-200 bg-white dark:bg-gray-900 dark:border-red-900 p-5">
          <h1 className="font-semibold text-gray-900 dark:text-white">{t('政策解读生成失败', 'Policy analysis failed')}</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{query.error instanceof Error ? query.error.message : t('请稍后重试', 'Please try again')}</p>
          <div className="mt-4 flex gap-2">
            <button onClick={() => navigate(-1)} className="px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-800 text-sm">{t('返回', 'Back')}</button>
            <button onClick={() => query.refetch()} className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm">{t('重试', 'Retry')}</button>
          </div>
        </div>
        </main>
      </div>
    )
  }

  const { analysis, sources } = query.data
  const relevanceLabel = analysis.regulatory_relevance === 'insufficient_evidence'
    ? t('证据不足', 'Insufficient evidence')
    : analysis.regulatory_relevance.toUpperCase()
  const sourceGroups = Array.from(
    sources.reduce((groups, source) => {
      const key = source.source_url || source.source_file || source.title
      const current = groups.get(key)
      if (current) current.citations.push(source.citation_id)
      else groups.set(key, { ...source, citations: [source.citation_id] })
      return groups
    }, new Map<string, PolicyAnalysisResponse['sources'][number] & { citations: string[] }>()).values()
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-24">
      {pageHeader}

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        {eventSection}
        <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{t('监管相关性', 'Regulatory relevance')}</h2>
            <span className="rounded px-2 py-1 text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">{relevanceLabel}</span>
          </div>
          <p className="mt-3 text-sm leading-6 text-gray-700 dark:text-gray-200">{analysis.summary}</p>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{t('合规性分析', 'Compliance analysis')}</h2>
          <p className="mt-2 text-sm leading-6 text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{analysis.compliance_analysis}</p>
          {analysis.limitations && <div className="mt-4 border-t border-gray-100 pt-3 text-xs leading-5 text-gray-500 dark:border-gray-800">{t('局限：', 'Limitations: ')}{analysis.limitations}</div>}
        </section>

        {analysis.applicable_regulations.length > 0 && <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{t('相关法规', 'Applicable regulations')}</h2>
          <div className="mt-2 divide-y divide-gray-100 dark:divide-gray-800">
            {analysis.applicable_regulations.map((item, index) => <div key={`${item.name}-${index}`} className="py-3 text-sm"><div className="font-medium text-gray-900 dark:text-white">{item.name}<Citations values={item.citations} /></div><p className="mt-1 text-gray-600 dark:text-gray-300">{item.relevance}</p></div>)}
          </div>
        </section>}

        {analysis.regulatory_risks.length > 0 && <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{t('潜在风险', 'Potential risks')}</h2>
          <div className="mt-2 space-y-2">{analysis.regulatory_risks.map((item, index) => <div key={index} className="text-sm text-gray-700 dark:text-gray-200"><span className="mr-2 rounded bg-amber-50 px-1.5 py-0.5 text-xs uppercase text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">{item.severity}</span>{item.risk}<Citations values={item.citations} /></div>)}</div>
        </section>}

        {analysis.recommendations.length > 0 && <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{t('合规建议', 'Recommendations')}</h2>
          <ol className="mt-2 space-y-2 list-decimal pl-5 text-sm text-gray-700 dark:text-gray-200">{analysis.recommendations.map((item, index) => <li key={index}>{item.recommendation}<Citations values={item.citations} /></li>)}</ol>
        </section>}

        {(messages.length > 0 || isSending) && <div className="space-y-2">
            {messages.map((message, index) => (
              <div key={index} className={`rounded-lg p-3 text-sm leading-6 whitespace-pre-wrap ${message.role === 'user' ? 'bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100' : 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-200'}`}>
                {message.content}
              </div>
            ))}
            {isSending && <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-500 dark:bg-gray-800">{t('正在结合政策证据分析...', 'Analyzing against the policy evidence...')}</div>}
        </div>}

        <section className="px-1 py-1">
          <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('政策来源', 'Policy sources')} ({sourceGroups.length})</h2>
          <div className="mt-1 space-y-1">
            {sourceGroups.map((source) => {
              const content = <>
                <div className="min-w-0">
                  <div className="leading-4 text-gray-700 dark:text-gray-300"><span className="font-medium text-blue-600 dark:text-blue-400">[{source.citations.join(', ')}]</span> {source.display_title || source.title}</div>
                  {(source.date || source.content_type) && <div className="text-[10px] leading-4 text-gray-400">{[source.date, source.content_type].filter(Boolean).join(' · ')}</div>}
                </div>
                {source.source_url && <span className="shrink-0 text-blue-600" aria-hidden="true">↗</span>}
              </>
              const className = "flex items-start justify-between gap-2 py-1 text-xs"
              return source.source_url ? (
                <a
                  key={source.source_url}
                  href={source.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { if (id) trackBusinessEvent('policy_source_expand', { event_id: id, citation_ids: source.citations, source_file: source.source_file }, id) }}
                  className={`${className} hover:text-blue-700 dark:hover:text-blue-300`}
                >{content}</a>
              ) : <div key={source.source_file || source.title} className={className}>{content}</div>
            })}
          </div>
        </section>
      </main>
      <form onSubmit={handleSubmit} className="fixed bottom-0 left-0 right-0 z-20 border-t border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
        <div className="mx-auto flex max-w-3xl gap-2">
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            disabled={!query.data.session_id || isSending}
            placeholder={t('继续询问该事件的政策影响...', 'Ask about the policy implications...')}
            className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          />
          <button disabled={!input.trim() || !query.data.session_id || isSending} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {t('发送', 'Send')}
          </button>
        </div>
      </form>
    </div>
  )
}
